import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Key, UsersThree, HardDrives, Plus, Trash, ShieldCheck, FloppyDisk, Folder } from "@phosphor-icons/react";
import { isFolderSupported, pickFolder, getFolderName, forgetFolder, getAutoSave, setAutoSave } from "@/lib/localFolder";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Configuration</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">Settings</h1>
        <p className="text-sm text-zinc-500 mt-2">Manage password, workers, drives and your local save folder.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PasswordCard />
        <LocalFolderCard />
        {isAdmin && <ShipmentLockCard />}
        {isAdmin && <WorkersCard />}
        <DrivesCard isAdmin={isAdmin} />
      </div>
    </Layout>
  );
}

function ShipmentLockCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/app-settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  const toggle = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const next = !settings.shipments_editable_by_workers;
      const { data } = await api.patch("/app-settings", { shipments_editable_by_workers: next });
      setSettings(data);
      toast.success(next ? "Workers can now edit shipments" : "Workers can no longer edit shipments");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const enabled = settings?.shipments_editable_by_workers ?? true;
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="shipment-lock-card">
      <SectionHeader icon={ShieldCheck} title="Shipment edit access" hint="Admins can always edit. Toggle whether non-admin users may edit shipment info." />
      <div className="flex items-center justify-between gap-3 pt-2">
        <div className="text-sm">
          <div className="font-medium text-zinc-900">Workers may edit shipments</div>
          <div className="text-xs text-zinc-500 mt-0.5">{enabled ? "Enabled — workers can edit AWB, consignee, dates, etc." : "Disabled — only admins can edit shipments."}</div>
        </div>
        <button type="button" onClick={toggle} disabled={saving || !settings} data-testid="shipment-lock-toggle"
          className={`relative inline-flex h-6 w-11 items-center transition-colors ${enabled ? "bg-emerald-600" : "bg-zinc-300"} disabled:opacity-50`}>
          <span className={`inline-block h-5 w-5 bg-white transform transition-transform ${enabled ? "translate-x-5" : "translate-x-1"}`}/>
        </button>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="w-9 h-9 bg-zinc-950 text-white flex items-center justify-center">
        <Icon size={16} weight="regular" />
      </div>
      <div>
        <div className="font-display font-black text-lg text-zinc-950 tracking-tight">{title}</div>
        {hint && <div className="text-xs text-zinc-500">{hint}</div>}
      </div>
    </div>
  );
}

function LocalFolderCard() {
  const [folderName, setFolderName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoSave, setAutoSaveState] = useState(false);
  const supported = isFolderSupported();

  useEffect(() => {
    getFolderName().then(setFolderName);
    getAutoSave().then(setAutoSaveState);
  }, []);

  const choose = async () => {
    setBusy(true);
    try {
      const h = await pickFolder();
      setFolderName(h.name);
      toast.success(`Folder set: ${h.name}`);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Could not pick folder");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    await forgetFolder();
    setFolderName(null);
    toast.success("Folder cleared");
  };

  const toggleAuto = async () => {
    const v = !autoSave;
    await setAutoSave(v);
    setAutoSaveState(v);
    toast.success(v ? "Auto-save enabled" : "Auto-save disabled");
  };

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="local-folder-card">
      <SectionHeader icon={Folder} title="Save to local folder"
        hint="After picking a folder (e.g. C:\CargoDocs\), the 'Save' button on each document writes directly to it — no browser dialog." />

      {!supported ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 px-3 py-2">
          <b>Not supported in this browser.</b> To save directly to your PC folder, use <b>Google Chrome</b> or <b>Microsoft Edge</b>.
          You can still download files normally (they go to your browser's Downloads folder).
        </div>
      ) : folderName ? (
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-1">Current folder</div>
          <div className="flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-3 py-2 mb-3">
            <FloppyDisk size={16} className="text-[#002FA7]" />
            <span className="font-mono text-sm text-zinc-900" data-testid="folder-name">{folderName}</span>
          </div>
          <div className="text-xs text-zinc-500 mb-3">
            Each shipment's files will be saved into a sub-folder named after its AWB number
            (e.g. <span className="font-mono">{folderName}\176-12345678\</span>).
          </div>

          {/* Auto-save toggle */}
          <label className="flex items-start gap-3 border border-zinc-200 bg-zinc-50 p-3 mb-3 cursor-pointer hover:bg-zinc-100 transition-colors duration-150" data-testid="auto-save-toggle-label">
            <input type="checkbox" checked={autoSave} onChange={toggleAuto}
              className="mt-0.5 accent-[#002FA7]" data-testid="auto-save-toggle" />
            <div>
              <div className="text-sm font-medium text-zinc-900">Auto-save when shipment is complete</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Writes all 5 required docs to your folder automatically the moment they're all uploaded.
                Duplicates are skipped — each file only writes once even if you re-open the shipment.
              </div>
            </div>
          </label>

          <div className="flex gap-2">
            <button onClick={choose} disabled={busy}
              className="bg-white border border-zinc-200 text-sm px-3 py-2 hover:bg-zinc-100 flex items-center gap-2"
              data-testid="change-folder-btn">
              Change folder
            </button>
            <button onClick={clear}
              className="text-sm text-red-700 border border-red-200 px-3 py-2 hover:bg-red-50 flex items-center gap-2"
              data-testid="clear-folder-btn">
              <Trash size={14} /> Clear
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-xs text-zinc-500 mb-3">
            Click below and pick (or create) a folder such as <span className="font-mono bg-zinc-100 px-1">C:\CargoDocs</span>.
            Your browser will ask you once to allow write access.
          </div>
          <button onClick={choose} disabled={busy}
            className="bg-[#002FA7] text-white text-sm px-4 py-2.5 hover:bg-[#00227A] disabled:opacity-60 flex items-center gap-2 transition-colors duration-150"
            data-testid="choose-folder-btn">
            <Folder size={16} /> {busy ? "Waiting…" : "Choose folder…"}
          </button>
        </div>
      )}
    </div>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6">
      <SectionHeader icon={Key} title="Change password" hint="Use at least 6 characters." />
      <form onSubmit={submit} className="space-y-3" data-testid="password-form">
        <input
          type="password" placeholder="Current password" required
          value={current} onChange={(e) => setCurrent(e.target.value)}
          className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
          data-testid="current-password-input"
        />
        <input
          type="password" placeholder="New password" required minLength={6}
          value={next} onChange={(e) => setNext(e.target.value)}
          className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
          data-testid="new-password-input"
        />
        <button
          type="submit" disabled={busy}
          className="bg-[#002FA7] text-white text-sm px-4 py-2.5 hover:bg-[#00227A] disabled:opacity-60 transition-colors duration-150 flex items-center gap-2"
          data-testid="change-password-btn"
        >
          <ShieldCheck size={16} /> {busy ? "Saving…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

function WorkersCard() {
  const [workers, setWorkers] = useState([]);
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setWorkers(data.filter((u) => u.role === "worker"));
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const addWorker = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", { name, email, password });
      toast.success("Worker added");
      setName(""); setEmail(""); setPassword("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeWorker = async (id) => {
    if (!window.confirm("Remove this worker?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Worker removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6">
      <SectionHeader icon={UsersThree} title="Workers" hint="Workers can upload and search but cannot manage users or drives." />
      <form onSubmit={addWorker} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4" data-testid="add-worker-form">
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Name"
          className="border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
          data-testid="worker-name-input" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
          className="border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
          data-testid="worker-email-input" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Password (min 6)"
          className="border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent md:col-span-2"
          data-testid="worker-password-input" />
        <button type="submit" disabled={busy}
          className="bg-[#002FA7] text-white text-sm px-4 py-2.5 hover:bg-[#00227A] disabled:opacity-60 transition-colors duration-150 flex items-center justify-center gap-2 md:col-span-2"
          data-testid="add-worker-btn">
          <Plus size={16} /> {busy ? "Adding…" : "Add worker"}
        </button>
      </form>
      <div className="border-t border-zinc-200 pt-3">
        {workers.length === 0 ? (
          <div className="text-sm text-zinc-500 font-mono py-3">No workers yet.</div>
        ) : workers.map((w) => (
          <div key={w.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0" data-testid={`worker-row-${w.id}`}>
            <div>
              <div className="text-sm font-medium text-zinc-900">{w.name}</div>
              <div className="text-xs text-zinc-500 font-mono">{w.email}</div>
            </div>
            <button onClick={() => removeWorker(w.id)} data-testid={`remove-worker-${w.id}`}
              className="text-xs text-red-700 flex items-center gap-1 hover:bg-red-50 px-2 py-1 transition-colors duration-150">
              <Trash size={12} /> Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DrivesCard({ isAdmin }) {
  const [drives, setDrives] = useState([]);
  const [letter, setLetter] = useState(""); const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/drives"); setDrives(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/drives", { letter: letter.toUpperCase(), label });
      toast.success(`Drive ${letter.toUpperCase()}: added`);
      setLetter(""); setLabel("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Remove drive ${l}:?`)) return;
    try { await api.delete(`/drives/${l}`); toast.success(`Drive ${l}: removed`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6">
      <SectionHeader icon={HardDrives} title="Drives" hint="Symbolic storage bins (like C:, D:) to organize documents." />

      {isAdmin && (
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4" data-testid="add-drive-form">
          <input value={letter} onChange={(e) => setLetter(e.target.value.toUpperCase().slice(0, 1))} required
            placeholder="Letter (A-Z)" maxLength={1}
            className="border border-zinc-200 px-3 py-2 text-sm uppercase font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            data-testid="drive-letter-input" />
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (optional)"
            className="border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            data-testid="drive-label-input" />
          <button type="submit" disabled={busy}
            className="bg-[#002FA7] text-white text-sm px-4 py-2.5 hover:bg-[#00227A] disabled:opacity-60 transition-colors duration-150 flex items-center justify-center gap-2"
            data-testid="add-drive-btn">
            <Plus size={16} /> {busy ? "Adding…" : "Add drive"}
          </button>
        </form>
      )}

      <div className="border-t border-zinc-200 pt-3">
        {drives.length === 0 ? (
          <div className="text-sm text-zinc-500 font-mono py-3">No drives configured.</div>
        ) : drives.map((d) => (
          <div key={d.letter} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0" data-testid={`drive-row-${d.letter}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-zinc-100 text-zinc-950 flex items-center justify-center font-mono font-bold">{d.letter}:</div>
              <div className="text-sm text-zinc-900">{d.label || <span className="text-zinc-400 italic">no label</span>}</div>
            </div>
            {isAdmin && (
              <button onClick={() => remove(d.letter)} data-testid={`remove-drive-${d.letter}`}
                className="text-xs text-red-700 flex items-center gap-1 hover:bg-red-50 px-2 py-1 transition-colors duration-150">
                <Trash size={12} /> Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
